import type { GeneralParameters } from '../../general/parameters/general'
import type { SessionStartParameters } from '../parameters/session-start'

import { Analytix } from '~/shared/analytix'
import { AnalytixEvent } from '../../general/event'
import { Gate, seeds } from '../../general/gate'
import { parameters } from '../parameters/session-start'

export class SessionStartEvent extends AnalytixEvent<SessionStartParameters> {
  name = 'session_start'
  group = null
  parameters = parameters
  sender = Analytix.prototype.sendSessionStartEvent

  // `session_start` event opens session gate behind, which means that no more duplicating
  // `session_start` events can be emitted until the gate is closed again on `session_end`
  gates = [Gate.Opens(seeds.session)]

  // `session_start` event MUST await the availability of `contentUserProfileName` parameter
  // before being sent, as it is required by analytix for user identification. Therefore,
  // we set an infinite timeout and only mark the event as ready when `contentUserProfileName` is set.
  override timeout = Infinity

  constructor() {
    super()

    // `session_start` event is triggered on authentication, but some parameters become available
    // only after fetching actual profile and subscription data. To ensure that the event is sent
    // only when all required parameters are available, we listen to updates on the general parameters
    // and re-verify event readiness.
    this.verify().then((ready) => {
      if (!ready) {
        const off = this.general.updates.on(() => this.verify())
        this.readiness.finally(off)
      }
    })
  }

  /**
   * Validate required parameters for `session_start` event.
   */
  validate(): false | [GeneralParameters, SessionStartParameters] {
    // valid conditions for `session_start` event:
    // - guest user (identified by negative `contentUserProfileId`) - in this case we don't have `subscriptionName` and `contentUserProfileName`, but the event should still be sent
    // - authenticated user with available `subscriptionName` and `contentUserProfileName`
    const valid =
      Number(this.general.get('contentUserProfileId')) < 0 || // guest user
      (this.general.get('subscriptionName') != null &&
        this.general.get('contentUserProfileName') != null)

    return (
      valid && [
        Object.fromEntries(this.general),
        Object.fromEntries(this.parameters),
      ]
    )
  }
}
